import { useMutation, useQuery, useQueryClient } from 'react-query';
import { QUERY_KEYS } from '../constants';
import { CreateUserPayload, User } from '../../types';
import { Http } from '../../services/Http';
import { AxiosError } from 'axios';

export type UsersListActiveFilter = 'all' | 'active' | 'inactive';

async function fetchUsers(isActiveFilter: UsersListActiveFilter = 'all'): Promise<User[]> {
  const params = new URLSearchParams();
  if (isActiveFilter === 'active') params.set('isActive', 'true');
  if (isActiveFilter === 'inactive') params.set('isActive', 'false');
  const query = params.toString();
  const path = query ? `/user?${query}` : `/user`;
  const { data } = await Http.axios.get<User[]>(path);
  return data;
}

export const useUsers = (isActiveFilter: UsersListActiveFilter = 'all') => {
  return useQuery([QUERY_KEYS.USERS, isActiveFilter], () => fetchUsers(isActiveFilter), {
    staleTime: Infinity,
    refetchInterval: false,
    refetchOnMount: true,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    refetchIntervalInBackground: false,
  });
};

export const useInvalidateUsers = () => {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.USERS],
      exact: false,
    });
  };
};

export async function fetchUserByEmail(email: string): Promise<User | null> {
  try {
    const response = await Http.axios.get<User>(`user/email/${email}`);
    return response.data;
  } catch (error) {
    if ((error as AxiosError)?.response?.status === 404) return null;
    throw error;
  }
}

async function createUser(payload: Partial<CreateUserPayload>): Promise<User> {
  const { data } = await Http.axios.post<Partial<CreateUserPayload>, User>(`/user`, payload);
  return data;
}

export function useCreateUser() {
  return useMutation(createUser);
}
